import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  BookOpen,
  ChevronDown,
  Clock,
  Compass,
  Dna,
  Download,
  FileText,
  Flame,
  Gem,
  GitBranch,
  Keyboard,
  LayoutDashboard,
  Lock,
  Mic,
  PenLine,
  QrCode,
  Settings,
  Shield,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { useOnboarding } from '../onboarding/OnboardingContext';
import { FEATURE_GUIDE_SECTIONS, STATUS_LABELS, AI_STORY_STUDIO_PILLAR, FAMILY_HANDOVER_VAULT_PILLAR, MEMORIAL_BRIDGE_PILLAR } from '../../content/featureGuideContent';
import { openBridgeAssistant } from '../../utils/bridgeAssistant';
import '../../styles/feature-guide.css';

const GUIDE_NAV = [
  { id: 'start-here', label: 'Start here' },
  { id: 'ai-story-studio', label: 'AI Story Studio' },
  { id: 'family-handover-vault', label: 'Family Handover Vault' },
  { id: 'memorial-bridge', label: 'Memorial Bridge' },
  { id: 'archive-basics', label: 'Archive basics' },
  { id: 'stories-voice', label: 'Stories & voice' },
  { id: 'storybook', label: 'Storybook' },
  { id: 'media-possessions', label: 'Photos & possessions' },
  { id: 'reminders', label: 'Reminders' },
  { id: 'exports', label: 'Legacy Book exports' },
  { id: 'memorials', label: 'Memorials' },
  { id: 'privacy-trust', label: 'Privacy & trust' },
  { id: 'dna', label: 'DNA beta' },
];

const SECTION_ICONS = {
  'archive-basics': LayoutDashboard,
  'stories-voice': Mic,
  storybook: BookOpen,
  'media-possessions': Gem,
  reminders: Bell,
  exports: Download,
  memorials: Flame,
  'privacy-trust': Shield,
  dna: Dna,
  community: Users,
};

const FEATURE_ICONS = {
  dashboard: LayoutDashboard,
  'add-yourself': UserPlus,
  'add-people': UserPlus,
  'family-tree': GitBranch,
  'people-list': Users,
  timeline: Clock,
  'archive-settings': Settings,
  'write-memory': PenLine,
  'record-voice': Mic,
  'continue-recording': Mic,
  transcribe: Keyboard,
  'polish-narrative': PenLine,
  'link-people': Users,
  'guided-prompts': BookOpen,
  chapters: BookOpen,
  'interview-mode': BookOpen,
  'voice-answers': Mic,
  'ai-chapter-draft': Sparkles,
  'storybook-export': Download,
  'preserve-documents': FileText,
  'upload-photos': FileText,
  priceless: Gem,
  'link-items': Gem,
  'legacy-book-include': Download,
  birthday: Bell,
  remembrance: Bell,
  anniversaries: Bell,
  'custom-dates': Bell,
  'digital-cards': Bell,
  'family-book': BookOpen,
  'person-book': BookOpen,
  'review-pdf': FileText,
  'storybook-pdf': Download,
  'json-backup': Download,
  'ai-book-prep': Sparkles,
  'memorial-workspace': Flame,
  'public-memorial': Flame,
  'tribute-wall': Flame,
  'qr-code': QrCode,
  keepsakes: QrCode,
  'support-link': Flame,
  'private-default': Shield,
  'invite-family': UserPlus,
  verification: Shield,
  visibility: Shield,
  'dna-privacy': Shield,
  'export-anytime': Download,
  'upload-dna': Dna,
  'storage-consent': Dna,
  'matching-consent': Dna,
  'early-matching': Dna,
  'no-medical': Shield,
  'org-archive': Users,
  'family-spaces': Users,
};

function scrollToSection(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function howToBullets(howTo, actionHint) {
  const parts = (howTo || '')
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 3);
  if (actionHint && parts.length < 3) {
    parts.push(actionHint);
  }
  return parts.slice(0, 3);
}

function getActionLabel(feature) {
  if (feature.status === 'planned') return 'Planned';
  if (feature.route) {
    if (feature.route === '/storybook') return 'Start';
    if (feature.route === '/faq') return 'Learn';
    if (feature.route === '/dashboard') return 'Open';
    return 'Open';
  }
  return feature.actionLabel || 'Open dashboard';
}

function StatusBadge({ status }) {
  if (status === 'available') return null;

  const styles = {
    beta: 'feature-guide-badge feature-guide-badge--beta',
    early_access: 'feature-guide-badge feature-guide-badge--early',
    planned: 'feature-guide-badge feature-guide-badge--planned',
  };

  return (
    <span className={cn('feature-guide-badge', styles[status] || styles.planned)}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function FeatureCard({ feature, sectionId, onOpenDashboard }) {
  const [expanded, setExpanded] = useState(false);
  const canOpen = Boolean(feature.route) && feature.status !== 'planned';
  const FeatureIcon = FEATURE_ICONS[feature.id] || SECTION_ICONS[sectionId] || Compass;
  const bullets = howToBullets(feature.howTo, feature.actionHint);
  const actionLabel = getActionLabel(feature);

  return (
    <article className="feature-guide-card" data-testid={`feature-guide-${feature.id}`}>
      <div className="feature-guide-card__row">
        <span className="feature-guide-card__icon" aria-hidden>
          <FeatureIcon className="h-5 w-5" strokeWidth={1.75} />
        </span>

        <div className="feature-guide-card__body">
          <div className="feature-guide-card__head">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="feature-guide-card__title">{feature.name}</h4>
                <StatusBadge status={feature.status} />
              </div>
              <p className="feature-guide-card__benefit">{feature.benefit}</p>
            </div>

            <div className="feature-guide-card__action">
              {canOpen ? (
                <Button size="sm" className="h-9 min-w-[5.5rem] rounded-full text-xs font-semibold" asChild>
                  <Link to={feature.route}>{actionLabel}</Link>
                </Button>
              ) : feature.status === 'planned' ? (
                <Button size="sm" variant="outline" className="h-9 min-w-[5.5rem] rounded-full text-xs" disabled>
                  Planned
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 min-w-[5.5rem] rounded-full text-xs"
                  onClick={onOpenDashboard}
                >
                  {actionLabel}
                </Button>
              )}
            </div>
          </div>

          <button
            type="button"
            className="feature-guide-card__toggle"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            How it works
            <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
          </button>

          {expanded ? (
            <div className="feature-guide-card__details">
              <ul className="list-disc space-y-1 pl-4 text-sm text-[hsl(32_10%_38%)]">
                {bullets.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {feature.route && feature.status !== 'planned' ? (
                <Link to={feature.route} className="mt-2 inline-block text-xs font-medium text-[hsl(152_28%_22%)] hover:underline">
                  Go to feature →
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function GuideCategoryNav({ activeId, onSelect, className }) {
  return (
    <nav className={cn('feature-guide-nav', className)} aria-label="Feature guide categories">
      <p className="feature-guide-nav__label">Browse</p>
      <ul className="feature-guide-nav__list">
        {GUIDE_NAV.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={cn('feature-guide-nav__link', activeId === item.id && 'feature-guide-nav__link--active')}
              onClick={() => onSelect(item.id)}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function StoryStudioPillarPanel() {
  const pillar = AI_STORY_STUDIO_PILLAR;

  return (
    <section
      id="ai-story-studio"
      className="scroll-mt-28 rounded-2xl border border-[hsl(38_14%_82%)] bg-gradient-to-br from-[hsl(42_28%_99%)] to-[hsl(40_22%_97%)] p-6 sm:p-7"
      data-testid="feature-guide-story-studio-pillar"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
        Core product pillar
      </p>
      <h2 className="mt-1 font-serif text-2xl font-light text-[hsl(24_14%_16%)]">{pillar.title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[hsl(32_10%_38%)]">{pillar.tagline}</p>
      <p className="mt-2 text-xs text-[hsl(32_10%_42%)]">{pillar.safety}</p>
      <ul className="mt-4 flex flex-wrap gap-2">
        {pillar.features.map((item) => (
          <li
            key={item}
            className="rounded-full border border-[hsl(38_14%_82%)] bg-white/80 px-3 py-1 text-xs text-[hsl(32_10%_38%)]"
          >
            {item}
          </li>
        ))}
      </ul>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button size="sm" className="rounded-full" asChild>
          <Link to="/story-studio">Open Story Studio</Link>
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" asChild>
          <Link to="/storybook">Open Storybook</Link>
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" asChild>
          <Link to="/guide#exports">Preview Legacy Book</Link>
        </Button>
      </div>
    </section>
  );
}

function HandoverVaultPillarPanel() {
  const pillar = FAMILY_HANDOVER_VAULT_PILLAR;

  return (
    <section
      id="family-handover-vault"
      className="scroll-mt-28 rounded-2xl border border-[hsl(38_14%_82%)] bg-gradient-to-br from-[hsl(42_28%_99%)] to-[hsl(40_22%_97%)] p-6 sm:p-7"
      data-testid="feature-guide-handover-vault-pillar"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
        Core product pillar
      </p>
      <h2 className="mt-1 font-serif text-2xl font-light text-[hsl(24_14%_16%)]">{pillar.title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[hsl(32_10%_38%)]">{pillar.tagline}</p>
      <p className="mt-2 text-xs text-[hsl(32_10%_42%)]">{pillar.safety}</p>
      <ul className="mt-4 flex flex-wrap gap-2">
        {pillar.features.map((item) => (
          <li
            key={item}
            className="rounded-full border border-[hsl(38_14%_82%)] bg-white/80 px-3 py-1 text-xs text-[hsl(32_10%_38%)]"
          >
            {item}
          </li>
        ))}
      </ul>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button size="sm" className="rounded-full" asChild>
          <Link to="/vault">Open Vault</Link>
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" asChild>
          <Link to="/reminders">Review reminders</Link>
        </Button>
      </div>
    </section>
  );
}

function MemorialBridgePillarPanel() {
  const pillar = MEMORIAL_BRIDGE_PILLAR;

  return (
    <section
      id="memorial-bridge"
      className="scroll-mt-28 rounded-2xl border border-[hsl(38_14%_82%)] bg-gradient-to-br from-[hsl(42_28%_99%)] to-[hsl(40_22%_97%)] p-6 sm:p-7"
      data-testid="feature-guide-memorial-bridge-pillar"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
        Core product pillar
      </p>
      <h2 className="mt-1 font-serif text-2xl font-light text-[hsl(24_14%_16%)]">{pillar.title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[hsl(32_10%_38%)]">{pillar.tagline}</p>
      <p className="mt-2 text-xs text-[hsl(32_10%_42%)]">{pillar.safety}</p>
      <ul className="mt-4 flex flex-wrap gap-2">
        {pillar.features.map((item) => (
          <li
            key={item}
            className="rounded-full border border-[hsl(38_14%_82%)] bg-white/80 px-3 py-1 text-xs text-[hsl(32_10%_38%)]"
          >
            {item}
          </li>
        ))}
      </ul>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button size="sm" className="rounded-full" asChild>
          <Link to="/memorials">Open Memorials</Link>
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" asChild>
          <Link to="/guide#memorials">Learn about Memorial Bridge</Link>
        </Button>
      </div>
    </section>
  );
}

function StartHerePanel({ onGuidedSetup }) {
  const steps = [
    'Add yourself as a person',
    'Add your first family member',
    'Record or write one memory',
    'Preview your Legacy Book',
  ];

  return (
    <section id="start-here" className="feature-guide-start scroll-mt-28">
      <h2 className="feature-guide-section__title">New here? Start with these.</h2>
      <ol className="mt-4 space-y-2">
        {steps.map((step, i) => (
          <li key={step} className="flex gap-3 text-sm text-[hsl(32_10%_38%)]">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(152_28%_22%_/_0.1)] text-xs font-semibold text-[hsl(152_28%_22%)]">
              {i + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button type="button" className="rounded-full" onClick={onGuidedSetup}>
          Start guided setup
        </Button>
        <Button type="button" variant="outline" className="rounded-full" asChild>
          <Link to="/dashboard">Open dashboard</Link>
        </Button>
      </div>
    </section>
  );
}

export function FeatureGuideContent({ className = '' }) {
  const navigate = useNavigate();
  const { requestWalkthroughReplay } = useOnboarding();
  const [activeNav, setActiveNav] = useState('start-here');

  const goToSection = useCallback((id) => {
    setActiveNav(id);
    scrollToSection(id);
  }, []);

  const startGuidedSetup = () => {
    requestWalkthroughReplay();
    navigate('/dashboard');
  };

  const openDashboard = () => navigate('/dashboard');

  return (
    <div className={cn('feature-guide', className)} data-testid="feature-guide-content">
      <GuideCategoryNav
        activeId={activeNav}
        onSelect={goToSection}
        className="mb-8 lg:hidden"
      />

      <div className="feature-guide__grid">
        <div className="feature-guide__main space-y-12">
          <div className="feature-guide-intro">
            <p>
              Private by default. Built for stories, voices, documents, memorials and Legacy Books.
            </p>
            <p className="mt-2 text-xs text-[hsl(32_10%_42%)]">
              Features marked Beta, Early access or Planned are labelled once per card — everything else is available in testing.
            </p>
          </div>

          <div className="feature-guide-assistant">
            <p className="text-sm text-[hsl(32_10%_38%)]">Need help finding something?</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2 rounded-full border-[hsl(38_14%_82%)]"
              onClick={openBridgeAssistant}
              data-testid="guide-open-bridge-assistant"
            >
              <Sparkles className="mr-2 h-4 w-4 text-[#c9a961]" strokeWidth={1.75} />
              Open Bridge Assistant
            </Button>
          </div>

          <StoryStudioPillarPanel />

          <HandoverVaultPillarPanel />

          <MemorialBridgePillarPanel />

          <StartHerePanel onGuidedSetup={startGuidedSetup} />

          {FEATURE_GUIDE_SECTIONS.filter((s) => s.id !== 'community').map((section) => {
            const SectionIcon = SECTION_ICONS[section.id] || Compass;
            return (
              <section key={section.id} id={section.id} className="scroll-mt-28">
                <header className="feature-guide-section__header">
                  <span className="feature-guide-section__icon" aria-hidden>
                    <SectionIcon className="h-6 w-6" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="feature-guide-section__title">{section.title}</h2>
                    <p className="feature-guide-section__intro">{section.intro}</p>
                  </div>
                </header>
                <div className="mt-5 space-y-3">
                  {section.features.map((feature) => (
                    <FeatureCard
                      key={feature.id}
                      feature={feature}
                      sectionId={section.id}
                      onOpenDashboard={openDashboard}
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {FEATURE_GUIDE_SECTIONS.filter((s) => s.id === 'community').map((section) => {
            const SectionIcon = SECTION_ICONS[section.id] || Users;
            return (
              <section key={section.id} id={section.id} className="scroll-mt-28 opacity-90">
                <header className="feature-guide-section__header">
                  <span className="feature-guide-section__icon" aria-hidden>
                    <SectionIcon className="h-6 w-6" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="feature-guide-section__title">{section.title}</h2>
                    <p className="feature-guide-section__intro">{section.intro}</p>
                  </div>
                </header>
                <div className="mt-5 space-y-3">
                  {section.features.map((feature) => (
                    <FeatureCard
                      key={feature.id}
                      feature={feature}
                      sectionId={section.id}
                      onOpenDashboard={openDashboard}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <aside className="feature-guide__aside hidden lg:block">
          <GuideCategoryNav activeId={activeNav} onSelect={goToSection} />
        </aside>
      </div>
    </div>
  );
}

export default FeatureGuideContent;
